import React from 'react';
import { ScoreDisplay } from './ScoreDisplay';

/**
 * Test Component for ScoreDisplay with Team Logos
 * Simulates different scenarios: with logos, without logos, no team data
 */
export const ScoreDisplayTest = () => {
  const [scenario, setScenario] = React.useState('barcelona-madrid');
  const [scoreboardVisible, setScoreboardVisible] = React.useState(false);

  // Mock data scenarios
  const scenarios = {
    'barcelona-madrid': {
      score: { home: 2, away: 1 },
      homeTeam: { 
        id: 1, 
        name: 'FC Barcelona', 
        short_name: 'barcelona',
        logo_url: null 
      },
      awayTeam: { 
        id: 2, 
        name: 'Real Madrid', 
        short_name: 'madrid',
        logo_url: null 
      }
    },
    'no-logos': {
      score: { home: 0, away: 0 },
      homeTeam: { 
        id: 3, 
        name: 'Atletico Madrid', 
        short_name: 'ATM',
        logo_url: null 
      },
      awayTeam: { 
        id: 4, 
        name: 'Sevilla FC', 
        short_name: 'SEV',
        logo_url: null 
      }
    },
    'no-short-names': {
      score: { home: 1, away: 1 },
      homeTeam: { 
        id: 5, 
        name: 'Valencia CF', 
        short_name: null,
        logo_url: null 
      },
      awayTeam: { 
        id: 6, 
        name: 'Real Sociedad', 
        short_name: null,
        logo_url: null 
      }
    },
    'null-teams': {
      score: { home: 0, away: 0 },
      homeTeam: null,
      awayTeam: null
    }
  };

  const currentScenario = scenarios[scenario];

  const handleUpdateScore = (home, away) => {
    console.log('Score updated:', { home, away });
    alert(`Score updated to ${home} - ${away}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">ScoreDisplay Component Test</h1>

        {/* Scenario Selector */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="font-semibold mb-3">Test Scenarios:</h2>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setScenario('barcelona-madrid')}
              className={`px-4 py-2 rounded ${
                scenario === 'barcelona-madrid' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              Barcelona vs Madrid (with logos)
            </button>
            <button
              onClick={() => setScenario('no-logos')}
              className={`px-4 py-2 rounded ${
                scenario === 'no-logos' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              No Logos (initials)
            </button>
            <button
              onClick={() => setScenario('no-short-names')}
              className={`px-4 py-2 rounded ${
                scenario === 'no-short-names' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              No Short Names
            </button>
            <button
              onClick={() => setScenario('null-teams')}
              className={`px-4 py-2 rounded ${
                scenario === 'null-teams' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              Null Teams (fallback)
            </button>
          </div>
        </div>

        {/* Current Scenario Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold mb-2">Current Scenario Data:</h3>
          <pre className="text-sm overflow-auto">
            {JSON.stringify(currentScenario, null, 2)}
          </pre>
        </div>

        {/* Component Under Test */}
        <ScoreDisplay
          score={currentScenario.score}
          homeTeam={currentScenario.homeTeam}
          awayTeam={currentScenario.awayTeam}
          onUpdateScore={handleUpdateScore}
          scoreboardVisible={scoreboardVisible}
          onToggleScoreboard={() => setScoreboardVisible(!scoreboardVisible)}
          joined={true}
        />

        {/* Test Notes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
          <h3 className="font-semibold mb-2">🧪 What to Test:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Barcelona vs Madrid:</strong> Should show both team logos (64×64px)</li>
            <li><strong>No Logos:</strong> Should show colored circles with initials (ATM, SEV)</li>
            <li><strong>No Short Names:</strong> Should use full names and initials</li>
            <li><strong>Null Teams:</strong> Should show "HOME" / "AWAY" with default initials</li>
            <li><strong>Scoreboard Toggle:</strong> Button should work and show timer</li>
            <li><strong>Manual Override:</strong> Expand and test score update</li>
          </ul>
        </div>

        {/* Logo Files Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <h3 className="font-semibold mb-2">📁 Logo Files:</h3>
          <p className="text-sm mb-2">Located at: <code className="bg-white px-2 py-1 rounded">frontend/public/images/teams/</code></p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><code>barcelona.png</code> - FC Barcelona logo</li>
            <li><code>madrid.png</code> - Real Madrid logo</li>
          </ul>
          <p className="text-xs text-gray-600 mt-2">
            Logos are loaded as: <code>/images/teams/[short_name].png</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScoreDisplayTest;
